import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Avatar,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Divider,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosInstance from "../../../../../../services/axiosInstance";
import apiEndPointsConfig from "../../../../../../services/apiEndPoints";
import {
  AttachmentPreviewDialog,
  AttachmentThumbnail,
  getAttachmentKind,
  isPreviewable,
  useAttachmentFiles,
} from "./AttachmentPreview";

// Note text is optional when the note carries attachments (checked on submit)
const validationSchema = Yup.object({
  note: Yup.string().optional(),
  title: Yup.string().optional(),
});

// Must match NOTE_ATTACHMENT_LIMITS / NOTE_ATTACHMENT_TYPES in the API's upload middleware
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_MB = 10;
const ATTACHMENT_ACCEPT =
  "image/jpeg,image/png,image/gif,image/webp,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip";

const formatSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round((bytes || 0) / 1024))} KB`;

const NotesForm = ({ dealId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingNotes, setFetchingNotes] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  // Attachment preview: thumbnails/blob URLs and the open modal
  const attachmentFiles = useAttachmentFiles();
  const [preview, setPreview] = useState(null); // { noteId, attachmentId }

  // Popup states
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [noteDescription, seNoteDescription] = useState("");
  console.log(noteDescription, "-0-0");

  const userId = localStorage.getItem("userId");

  // Fetch notes when dealId changes
  useEffect(() => {
    if (dealId) {
      fetchNotes();
    }
  }, [dealId]);

  const fetchNotes = async () => {
    try {
      setFetchingNotes(true);
      setError(null);

      const response = await axiosInstance.get(
        `${apiEndPointsConfig.getAllNotes}/${dealId}`
      );

      setNotes(response.data);
    } catch (err) {
      setError("Failed to load notes. Please try again.");
      console.error("Error fetching notes:", err);
    } finally {
      setFetchingNotes(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      note: "",
      title: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleSubmit(values);
    },
  });

  const addDealTimeline = async (payload) => {
    
    try {
      await axiosInstance.post(apiEndPointsConfig.createTimeline, payload);
    } catch (error) {
      console.error("Error creating deal timeline:", error);
    }
  };

  const addAttachments = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    const tooBig = incoming.filter(
      (f) => f.size > MAX_ATTACHMENT_MB * 1024 * 1024
    );
    const accepted = incoming.filter((f) => !tooBig.includes(f));
    const room = MAX_ATTACHMENTS - attachments.length;

    if (tooBig.length) {
      setError(
        `${tooBig.map((f) => f.name).join(", ")} exceeds the ${MAX_ATTACHMENT_MB} MB limit.`
      );
    } else if (accepted.length > room) {
      setError(`You can attach up to ${MAX_ATTACHMENTS} files per note.`);
    }

    setAttachments((prev) => [...prev, ...accepted.slice(0, Math.max(room, 0))]);
    setIsFormExpanded(true);
  };

  // Lets users paste a screenshot (e.g. a WhatsApp chat) straight into the note
  const handlePaste = (e) => {
    const pasted = Array.from(e.clipboardData?.files || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (!pasted.length) return;
    e.preventDefault();
    addAttachments(
      pasted.map(
        (f, i) =>
          new File([f], `screenshot-${Date.now()}-${i + 1}.png`, { type: f.type })
      )
    );
  };

  const removeAttachment = (index) =>
    setAttachments((prev) => prev.filter((_, i) => i !== index));

  const resetComposer = () => {
    formik.resetForm();
    setAttachments([]);
    setIsFormExpanded(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (values) => {
    if (!values.note.trim() && !attachments.length) return;

    try {
      setLoading(true);
      setError(null);

      const formData = new FormData();
      formData.append("dealId", dealId);
      formData.append("note", values.note);
      formData.append("title", values.title);
      formData.append("userId", userId);
      attachments.forEach((file) => formData.append("attachments", file));

      const attachmentSummary = attachments.length
        ? `${attachments.length} attachment${attachments.length > 1 ? "s" : ""}: ${attachments.map((f) => f.name).join(", ")}`
        : "";

      const timelinePayload = {
        dealId,
        title: "Note added",
        description: [values.note, attachmentSummary].filter(Boolean).join("\n"),
        userId,
        changeTime: new Date(),
      };

      const response = await axiosInstance.post(
        apiEndPointsConfig.addNotes,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const noteData = response.data.note || response.data;

      setNotes((prevNotes) => [noteData, ...prevNotes]);

      addDealTimeline(timelinePayload);

      resetComposer();
      setSuccessMessage("Note saved successfully!");

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to save note. Please try again."
      );
      console.error("Error creating note:", err);
    } finally {
      setLoading(false);
    }
  };

  // Note attachments as preview items; local-disk ones load through the
  // authenticated download route
  const toItems = (note) =>
    (note.attachments || []).map((attachment) => ({
      ...attachment,
      id: attachment._id,
      fetchBlob: async () =>
        (
          await axiosInstance.get(
            `${apiEndPointsConfig.downloadNoteAttachment}/${note._id}/${attachment._id}`,
            { responseType: "blob" }
          )
        ).data,
    }));

  useEffect(() => {
    attachmentFiles.loadThumbnails(notes.flatMap(toItems));
  }, [notes]);

  const previewNote = preview && notes.find((n) => n._id === preview.noteId);

  // Images, PDFs and audio open in the preview modal; anything else downloads
  const openAttachment = (item, note) => {
    if (isPreviewable(item)) {
      setPreview({ noteId: note._id, attachmentId: item.id });
      return;
    }
    attachmentFiles.download(item).catch((err) => {
      console.error("Error downloading attachment:", err);
      setError("Failed to download attachment.");
    });
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const timelinePayload = {
        dealId,
        title: "Note Deleted",
        description: noteDescription,
        userId,
        changeTime: new Date(),
      };
      const response = await axiosInstance.delete(
        `${apiEndPointsConfig.deleteNotes}/${noteId}`
      );

      setNotes(notes.filter((note) => note._id !== noteId));
      addDealTimeline(timelinePayload);

      setSuccessMessage("Note deleted successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError("Failed to delete note. Please try again.");
      console.error("Error deleting note:", err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getInitial = (name) => (name ? name.charAt(0).toUpperCase() : "U");

  if (!dealId) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Please select a deal to add notes</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", px: { xs: 0, sm: 1 }, py: 1 }}>
      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {/* Note Form */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          bgcolor: "#f9fafb",
          p: 2,
          mb: 3,
          border: "1px solid #e5e7eb",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1.5,
            gap: 1,
          }}
        >
          <Box
            sx={{
              bgcolor: "#eff6ff",
              borderRadius: "50%",
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <NoteAltIcon sx={{ fontSize: 18, color: "#2563eb" }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Add a note
            </Typography>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              Keep track of important updates related to this deal. Attach
              screenshots, call logs or documents, or paste a screenshot.
            </Typography>
          </Box>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <TextField
            fullWidth
            placeholder="Write your note here..."
            name="note"
            value={formik.values.note}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            onFocus={() => setIsFormExpanded(true)}
            onPaste={handlePaste}
            variant="outlined"
            disabled={loading}
            error={formik.touched.note && !!formik.errors.note}
            helperText={formik.touched.note && formik.errors.note}
            multiline
            minRows={isFormExpanded ? 3 : 1}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "#ffffff",
                borderRadius: 2,
              },
            }}
          />

          <Collapse in={isFormExpanded} timeout="auto">
            <Box sx={{ mt: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add a title (optional)"
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={loading}
                sx={{
                  mb: 1.5,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#ffffff",
                    borderRadius: 2,
                  },
                }}
              />

              {attachments.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                  {attachments.map((file, index) => (
                    <Chip
                      key={`${file.name}-${index}`}
                      size="small"
                      icon={<AttachFileIcon sx={{ fontSize: 16 }} />}
                      label={`${file.name} (${formatSize(file.size)})`}
                      onDelete={loading ? undefined : () => removeAttachment(index)}
                      sx={{ bgcolor: "#ffffff", border: "1px solid #e5e7eb", maxWidth: 260 }}
                    />
                  ))}
                </Box>
              )}

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                style={{ display: "none" }}
                onChange={(e) => {
                  addAttachments(e.target.files);
                  e.target.value = "";
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Button
                  size="small"
                  startIcon={<AttachFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading || attachments.length >= MAX_ATTACHMENTS}
                  sx={{ textTransform: "none", mr: "auto" }}
                >
                  Attach files
                  <Typography
                    component="span"
                    sx={{ fontSize: 11, color: "#9ca3af", ml: 0.75 }}
                  >
                    max {MAX_ATTACHMENTS}, {MAX_ATTACHMENT_MB} MB each
                  </Typography>
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={resetComposer}
                  disabled={loading}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  type="submit"
                  disabled={
                    loading || (!formik.values.note.trim() && !attachments.length)
                  }
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    "Save Note"
                  )}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </form>
      </Paper>

      {/* Notes List */}
      <Box sx={{ mt: 3 }}>
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            All Notes ({notes.length})
          </Typography>
        </Box>

        {fetchingNotes ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : notes.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: "1px dashed #e5e7eb",
              bgcolor: "#f9fafb",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#6b7280", fontStyle: "italic" }}
            >
              No notes yet. Add one to get started.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {notes.map((note) => (
              <Paper
                key={note._id}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #e5e7eb",
                  bgcolor: "#ffffff",
                  display: "flex",
                  gap: 1.5,
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    borderTopLeftRadius: 8,
                    borderBottomLeftRadius: 8,
                    bgcolor: "#22c55e",
                  },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "#22c55e",
                    width: 32,
                    height: 32,
                    mt: 0.5,
                  }}
                >
                  {getInitial(note.userName || "U")}
                </Avatar>

                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                        {note.userName || "Unknown"}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 11, color: "#6b7280", mt: 0.25 }}
                      >
                        {formatDate(note.createdAt)}
                      </Typography>
                    </Box>

                    <Tooltip title="Delete note">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setNoteToDelete(note._id);
                          seNoteDescription(note.note);
                          setOpenDeletePopup(true);
                        }}
                        sx={{
                          color: "#ef4444",
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {note.title && (
                    <Typography
                      sx={{
                        fontWeight: 600,
                        mt: 1,
                        fontSize: 14,
                      }}
                    >
                      {note.title}
                    </Typography>
                  )}

                  {note.note && (
                    <Typography sx={{ mt: 0.75, fontSize: 14, whiteSpace: "pre-wrap" }}>
                      {note.note}
                    </Typography>
                  )}

                  {note.attachments?.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.25 }}>
                      {toItems(note).map((item) =>
                        getAttachmentKind(item) === "image" ? (
                          <Tooltip key={item.id} title={item.fileName}>
                            <Box>
                              <AttachmentThumbnail
                                src={attachmentFiles.srcFor(item)}
                                alt={item.fileName}
                                onClick={() => openAttachment(item, note)}
                              />
                            </Box>
                          </Tooltip>
                        ) : (
                          <Chip
                            key={item.id}
                            size="small"
                            clickable
                            onClick={() => openAttachment(item, note)}
                            icon={
                              getAttachmentKind(item) === "audio" ? (
                                <AudiotrackIcon sx={{ fontSize: 16 }} />
                              ) : getAttachmentKind(item) === "pdf" ? (
                                <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <InsertDriveFileIcon sx={{ fontSize: 16 }} />
                              )
                            }
                            label={`${item.fileName} (${formatSize(item.fileSize)})`}
                            sx={{ border: "1px solid #e5e7eb", bgcolor: "#f9fafb", maxWidth: 260 }}
                          />
                        )
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      {/* Attachment Preview */}
      <AttachmentPreviewDialog
        items={previewNote ? toItems(previewNote) : []}
        activeId={preview?.attachmentId}
        onChange={(attachmentId) =>
          setPreview((prev) => ({ ...prev, attachmentId }))
        }
        onClose={() => setPreview(null)}
        files={attachmentFiles}
        onError={setError}
      />

      {/* Delete Confirmation Popup */}
      <Dialog
        open={openDeletePopup}
        onClose={() => setOpenDeletePopup(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            p: 1.5,
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          Delete Note?
        </DialogTitle>

        <Divider sx={{ m: 0 }} />

        <DialogContent sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
          <DialogContentText sx={{ fontSize: 14 }}>
            Are you sure you want to delete this note? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            p: 1.5,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => setOpenDeletePopup(false)}
            variant="outlined"
            size="small"
          >
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            size="small"
            onClick={() => {
              handleDeleteNote(noteToDelete);
              setOpenDeletePopup(false);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotesForm;
